import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Bell, Calendar, XCircle, Save, AlertTriangle, KeyRound } from 'lucide-react';
import apiClient from '../../api/client';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    no_show_charge_enabled: true,
    no_show_deduct_amount: 1,
    reservation_available_days: 14,
    reservation_cutoff_minutes: 30,
    cancel_cutoff_minutes: 60,
    same_day_cancel_deduct_enabled: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/admin/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('정책 설정을 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiClient.put('/admin/settings', settings);
      setSettings(response.data);
      toast.success('정책 설정이 저장되었습니다');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('정책 설정 저장에 실패했습니다');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // -------- 관리자 비밀번호 변경 --------
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const handleChangePassword = async () => {
    if (!pwCurrent || !pwNew || !pwConfirm) {
      toast.error('모든 항목을 입력해주세요');
      return;
    }
    if (pwNew.length < 8) {
      toast.error('새 비밀번호는 최소 8자 이상이어야 합니다');
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error('새 비밀번호와 확인이 일치하지 않습니다');
      return;
    }
    if (pwNew === pwCurrent) {
      toast.error('새 비밀번호는 현재 비밀번호와 달라야 합니다');
      return;
    }
    setPwSaving(true);
    try {
      await apiClient.post('/admin/auth/change-password', {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      toast.success('비밀번호가 변경되었습니다');
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : '비밀번호 변경에 실패했습니다';
      toast.error(msg);
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <SettingsIcon className="h-6 w-6" />
            정책 설정
          </h1>
          <p className="text-gray-500 mt-1">운영 정책을 설정합니다. 변경사항은 즉시 반영됩니다.</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="save-settings-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? '저장 중...' : '저장'}
        </Button>
      </div>

      {/* 노쇼 정책 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            노쇼 정책
          </CardTitle>
          <CardDescription>
            예약 후 출석하지 않은 회원에 대한 정책을 설정합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 노쇼 차감 여부 */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-medium">노쇼 시 이용권 차감</Label>
              <p className="text-sm text-gray-500">
                노쇼 처리 시 이용권을 자동으로 차감할지 설정합니다.
              </p>
            </div>
            <Switch
              checked={settings.no_show_charge_enabled}
              onCheckedChange={(checked) => handleChange('no_show_charge_enabled', checked)}
              data-testid="no-show-charge-switch"
            />
          </div>

          {/* 노쇼 차감 횟수 */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-medium">노쇼 차감 횟수</Label>
              <p className="text-sm text-gray-500">
                노쇼 1회당 차감할 이용권 횟수를 설정합니다.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                max="10"
                value={settings.no_show_deduct_amount}
                onChange={(e) => handleChange('no_show_deduct_amount', parseInt(e.target.value) || 0)}
                className="w-20 text-center"
                disabled={!settings.no_show_charge_enabled}
                data-testid="no-show-deduct-amount"
              />
              <span className="text-gray-500">회</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 예약 정책 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-blue-500" />
            예약 정책
          </CardTitle>
          <CardDescription>
            수업 예약에 대한 정책을 설정합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 예약 가능 기간 */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-medium">예약 가능 기간</Label>
              <p className="text-sm text-gray-500">
                오늘 기준 몇 일 뒤까지 수업 예약이 가능한지 설정합니다.
              </p>
              <p className="text-xs text-blue-600">
                예: 14일 → 오늘부터 2주 뒤까지 예약 가능
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="1"
                max="365"
                value={settings.reservation_available_days}
                onChange={(e) => handleChange('reservation_available_days', parseInt(e.target.value) || 14)}
                className="w-20 text-center"
                data-testid="reservation-available-days"
              />
              <span className="text-gray-500">일</span>
            </div>
          </div>

          {/* 예약 마감 시간 */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-medium">예약 마감 시간</Label>
              <p className="text-sm text-gray-500">
                수업 시작 몇 분 전까지 예약이 가능한지 설정합니다.
              </p>
              <p className="text-xs text-blue-600">
                예: 30분 → 수업 시작 30분 전까지 예약 가능
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">수업 시작</span>
              <Input
                type="number"
                min="0"
                max="1440"
                value={settings.reservation_cutoff_minutes}
                onChange={(e) => handleChange('reservation_cutoff_minutes', parseInt(e.target.value) || 0)}
                className="w-20 text-center"
                data-testid="reservation-cutoff-minutes"
              />
              <span className="text-gray-500">분 전까지</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 취소 정책 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <XCircle className="h-5 w-5 text-orange-500" />
            취소 정책
          </CardTitle>
          <CardDescription>
            예약 취소에 대한 정책을 설정합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 취소 마감 시간 */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-medium">취소 마감 시간</Label>
              <p className="text-sm text-gray-500">
                수업 시작 몇 분 전까지 예약 취소가 가능한지 설정합니다.
              </p>
              <p className="text-xs text-blue-600">
                예: 60분 → 수업 시작 1시간 전까지 취소 가능
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">수업 시작</span>
              <Input
                type="number"
                min="0"
                max="1440"
                value={settings.cancel_cutoff_minutes}
                onChange={(e) => handleChange('cancel_cutoff_minutes', parseInt(e.target.value) || 0)}
                className="w-20 text-center"
                data-testid="cancel-cutoff-minutes"
              />
              <span className="text-gray-500">분 전까지</span>
            </div>
          </div>

          {/* 당일 취소 차감 여부 */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-medium">당일 취소 시 이용권 차감</Label>
              <p className="text-sm text-gray-500">
                취소 마감 시간 이후 당일 취소 시 이용권을 차감할지 설정합니다.
              </p>
            </div>
            <Switch
              checked={settings.same_day_cancel_deduct_enabled}
              onCheckedChange={(checked) => handleChange('same_day_cancel_deduct_enabled', checked)}
              data-testid="same-day-cancel-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* 관리자 비밀번호 변경 */}
      <Card data-testid="admin-password-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <KeyRound className="h-5 w-5 text-blue-600" />
            관리자 비밀번호 변경
          </CardTitle>
          <CardDescription>
            현재 로그인한 관리자 계정의 비밀번호를 변경합니다. 변경 후에도 현재 세션은 유지됩니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="pw-current" className="text-sm">현재 비밀번호</Label>
            <Input
              id="pw-current"
              type="password"
              autoComplete="current-password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              className="mt-1.5 max-w-md"
              data-testid="pw-current"
              disabled={pwSaving}
            />
          </div>
          <div>
            <Label htmlFor="pw-new" className="text-sm">새 비밀번호 <span className="text-xs text-gray-500">(최소 8자)</span></Label>
            <Input
              id="pw-new"
              type="password"
              autoComplete="new-password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              className="mt-1.5 max-w-md"
              data-testid="pw-new"
              disabled={pwSaving}
            />
          </div>
          <div>
            <Label htmlFor="pw-confirm" className="text-sm">새 비밀번호 확인</Label>
            <Input
              id="pw-confirm"
              type="password"
              autoComplete="new-password"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              className="mt-1.5 max-w-md"
              data-testid="pw-confirm"
              disabled={pwSaving}
            />
          </div>
          <div className="pt-2">
            <Button
              onClick={handleChangePassword}
              disabled={pwSaving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="pw-change-btn"
            >
              <KeyRound className="h-4 w-4 mr-2" />
              {pwSaving ? '변경 중…' : '비밀번호 변경'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 하단 저장 버튼 */}
      <div className="flex justify-end pt-4">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
          size="lg"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? '저장 중...' : '설정 저장'}
        </Button>
      </div>
    </div>
  );
};

export default Settings;
